import { z } from 'zod';
import { PERMISSIONS, ROLE_KEYS } from '../constants/permissions.js';
import { emailSchema, usPhoneSchema, uuidSchema } from './common.js';
/**
 * Password policy: length over composition rules. NIST SP 800-63B guidance —
 * long passphrases, no forced character classes, no forced rotation.
 */
export const passwordSchema = z
    .string()
    .min(12, 'Use at least 12 characters')
    .max(128, 'Password is too long');
/** Email + password sign-in — the one login method for every account,
 *  customer and staff alike. */
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Enter your password').max(128),
});
/** Registers a new, role-less (customer) account. Staff accounts are created
 *  from the back office, never through this endpoint. */
export const signupSchema = z
    .object({
    email: emailSchema,
    password: passwordSchema,
    name: z.string().trim().min(1).max(150),
    /** Optional mobile number (t1075). Required only when `smsOptIn` is set. */
    phone: usPhoneSchema.nullable().default(null),
    /** Ticking the text-message consent box; never granted by omission. */
    smsOptIn: z.boolean().default(false),
})
    .refine((c) => !c.smsOptIn || !!c.phone, {
    message: 'Enter a mobile number to receive texts',
    path: ['phone'],
});
export const forgotPasswordSchema = z.object({ email: emailSchema });
export const resetPasswordSchema = z.object({
    token: z.string().trim().min(20).max(200),
    newPassword: passwordSchema,
});
export const emailCodeSchema = z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter the 6-digit code we emailed you');
export const recoveryCodeSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}$/, 'Enter a recovery code (xxxx-xxxx-xxxx)');
/** Answers the 2FA challenge after a password: either the code just emailed
 *  to the staff member, or one of their backup recovery codes. */
export const verifySecondFactorSchema = z.object({
    code: z.union([emailCodeSchema, recoveryCodeSchema]),
});
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1).max(128),
    newPassword: passwordSchema,
})
    .refine((v) => v.currentPassword !== v.newPassword, {
    message: 'New password must differ from the current one',
    path: ['newPassword'],
});
/** Login outcome: either a full session, or a 2FA challenge that must be
 *  answered. `codeSent` is false when no code was emailed — the half-session
 *  still exists, so a recovery code is the only way in. `codeReason`
 *  distinguishes why: `send_failed` (no mail server configured, or the
 *  provider rejected it) vs. `too_many_codes` (this account already used up
 *  its hourly/daily allotment of codes). Absent when `codeSent` is true. */
export const loginResultSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('authenticated') }),
    z.object({
        status: z.literal('email_code_required'),
        codeSent: z.boolean(),
        codeReason: z.enum(['send_failed', 'too_many_codes']).optional(),
        /** Present only as 'sms' (t1111), when the code was texted; absent means
         *  it went by email — including for a staffer who prefers text but whose
         *  text could not be sent. */
        codeChannel: z.enum(['email', 'sms']).optional(),
        /** Last four digits of the phone a text went to; only with 'sms'. */
        codePhoneLast4: z.string().optional(),
    }),
]);
export const resendSecondFactorSchema = z.object({
    /** Ask for a specific channel; unsatisfiable requests fall back to email. */
    channel: z.enum(['email', 'sms']).optional(),
});
export const staffProfileSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    name: z.string(),
    role: z.enum(ROLE_KEYS),
    permissions: z.array(z.enum(PERMISSIONS)),
    lastLoginAt: z.iso.datetime().nullable(),
});
// ------------------------------------------------------- staff administration
export const createStaffSchema = z.object({
    email: emailSchema,
    name: z.string().trim().min(1).max(150),
    role: z.enum(ROLE_KEYS),
    /**
     * Set by whoever creates the account and handed over in person. The new
     * member of staff is forced to change it at first sign-in, so it never has
     * to be a secret that survives the walk to their desk.
     */
    temporaryPassword: z.string().min(12).max(200),
});
export const updateStaffSchema = z.object({
    name: z.string().trim().min(1).max(150).optional(),
    role: z.enum(ROLE_KEYS).optional(),
    isActive: z.boolean().optional(),
    twoFactorChannel: z.enum(['email', 'sms']).optional(),
    /** E.164 number sign-in codes are texted to; null clears it. */
    twoFactorPhone: usPhoneSchema.nullable().optional(),
});
export const staffListItemSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    name: z.string(),
    role: z.enum(ROLE_KEYS),
    isActive: z.boolean(),
    mustChangePassword: z.boolean(),
    /** Set while the account is locked out after repeated failed sign-ins. */
    lockedUntil: z.iso.datetime().nullable(),
    lastLoginAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    twoFactorChannel: z.enum(['email', 'sms']),
    twoFactorPhone: z.string().nullable(),
});
/** A session seen within this many minutes counts as "signed in now". */
export const STAFF_ACTIVE_WINDOW_MINUTES = 10;
export const activeStaffItemSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    role: z.enum(ROLE_KEYS),
    lastSeenAt: z.iso.datetime(),
});
export const roleSchema = z.object({
    key: z.enum(ROLE_KEYS),
    name: z.string(),
    description: z.string().nullable(),
    // Read back from the database (a raw SQL aggregate), so left as plain
    // strings rather than the input-side `z.enum(PERMISSIONS)` — validating
    // that every stored value is still in the catalog is not this schema's job.
    permissions: z.array(z.string()),
    staffCount: z.number().int(),
});
export const updateRolePermissionsSchema = z.object({
    permissions: z.array(z.enum(PERMISSIONS)).max(PERMISSIONS.length),
});
export const customerProfileSchema = z.object({
    id: uuidSchema,
    phone: z.string().nullable(),
    name: z.string().nullable(),
    email: z.string().nullable(),
});
export const sessionSummarySchema = z.object({
    id: uuidSchema,
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    createdAt: z.iso.datetime(),
    lastSeenAt: z.iso.datetime(),
    current: z.boolean(),
});
//# sourceMappingURL=auth.js.map